// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRight from "@keller/XDL/Icons/ArrowRight";
import { color, ellipsis } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ClientAuthoredContentProps {
    title?: string;
    imageUrl?: string;
    link?: { title?: string; url?: string };
}

type Props = ClientAuthoredContentProps & { width?: string | number };

interface ClientAuthoredContentStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    image?: ImgProps;
    link?: LinkPresentationProps;
}

const clientAuthoredContentStyles: ClientAuthoredContentStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            position: absolute;
            left: 100%;
            top: 0;
            background: ${color("common.ui12")};
            height: 100%;
            padding: 20px 16px;
            ${({ width }: any) => width && `width: ${width}px;`}
        `,
    },
    title: {
        variant: "h4",
        color: "text",
        css: css`
            margin-bottom: 20px;
            min-height: 25px;
            ${ellipsis(2)}
        `,
    },
    image: {
        width: 293,
        height: 227,
        css: css`
            max-width: 100%;
            height: auto;
            margin-bottom: 20px;
        `,
    },
    link: {
        icon: {
            iconProps: {
                src: ArrowRight,
                size: 18,
                color: "primary",
                css: css`
                    opacity: 0.33;
                    margin-left: 8px;
                `,
            },
            position: "right",
        },
        typographyProps: {
            size: 14,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 16px;
                ${ellipsis(1)}
            `,
        },
        css: css`
            max-width: 100%;
        `,
    },
};

const styles = clientAuthoredContentStyles;

const ClientAuthoredContent: FC<Props> = ({ title, link, imageUrl, width }) => {
    if (!title || !imageUrl) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...{ width }} data-test-selector="client-authored-content">
            <Typography {...styles.title}>{title}</Typography>
            <Img src={imageUrl} {...styles.image} />
            {link?.url && (
                <StyledWrapper>
                    <Link href={link?.url} {...styles.link}>
                        {link?.title || link?.url}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ClientAuthoredContent;
